// src/components/about/TechnicalCapabilities.tsx
import type { ComponentType } from 'react';
import { useReducedMotion } from 'framer-motion';
import { FaCloud, FaExchangeAlt, FaProjectDiagram } from 'react-icons/fa';
import { SiDotnet, SiPostgresql, SiReact } from 'react-icons/si';

import { aboutConfig, type AboutCapabilityIcon } from '../../config/aboutConfig';
import {
  CapabilitiesRoot,
  CapabilitiesContainer,
  SectionHeader,
  Eyebrow,
  EyebrowMark,
  Heading,
  Lede,
  Stack,
  Layer,
  LayerHead,
  LayerIndex,
  IconWrap,
  HeadText,
  LayerTitle,
  LayerRole,
  LayerBody,
  ChipList,
  Chip,
  ClusterDivider,
  ClusterLabel,
  CapabilitiesNote,
} from './TechnicalCapabilities.styles';
import { motionSet, STAGGER } from './motion';

const headingId = 'about-capabilities-heading';

const iconMap: Record<AboutCapabilityIcon, ComponentType> = {
  backend: SiDotnet,
  architecture: FaProjectDiagram,
  data: SiPostgresql,
  messaging: FaExchangeAlt,
  frontend: SiReact,
  cloud: FaCloud,
};

const TechnicalCapabilities = () => {
  const { eyebrow, heading, lede, additionalLabel, note, groups } = aboutConfig.capabilities;
  const reduceMotion = useReducedMotion() ?? false;

  const motion = motionSet(reduceMotion);
  const rise = motion.reveal;

  return (
    <CapabilitiesRoot aria-labelledby={headingId}>
      <CapabilitiesContainer>
        <SectionHeader
          variants={rise}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <Eyebrow>
            <EyebrowMark aria-hidden="true" />
            {eyebrow}
          </Eyebrow>
          <Heading id={headingId}>{heading}</Heading>
          <Lede>{lede}</Lede>
        </SectionHeader>

        <Stack
          variants={motion.group(STAGGER.list)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {groups.map((group) => {
            const Icon = iconMap[group.icon];
            const documented = group.items.filter((item) => item.documented);
            const additional = group.items.filter((item) => !item.documented);

            return (
              <Layer key={group.id} variants={rise}>
                <LayerHead>
                  <LayerIndex aria-hidden="true">{group.index}</LayerIndex>

                  <IconWrap aria-hidden="true">
                    <Icon />
                  </IconWrap>

                  <HeadText>
                    <LayerTitle>{group.title}</LayerTitle>
                    <LayerRole>{group.role}</LayerRole>
                  </HeadText>
                </LayerHead>

                <LayerBody>
                  <ChipList>
                    {documented.map((item) => (
                      <Chip key={item.label}>{item.label}</Chip>
                    ))}
                  </ChipList>

                  {additional.length > 0 && (
                    <>
                      <ClusterDivider aria-hidden="true" />
                      <ClusterLabel>{additionalLabel}</ClusterLabel>
                      <ChipList>
                        {additional.map((item) => (
                          <Chip key={item.label}>{item.label}</Chip>
                        ))}
                      </ChipList>
                    </>
                  )}
                </LayerBody>
              </Layer>
            );
          })}
        </Stack>

        <CapabilitiesNote>
          <strong>How to read this</strong> — {note}
        </CapabilitiesNote>
      </CapabilitiesContainer>
    </CapabilitiesRoot>
  );
};

export default TechnicalCapabilities;
